import type { ValueOf } from "type-fest"
import type { MessageRecordType } from '@/api/business/types/message'
import { MessageTypeConstants } from '@/api/business/types/message'
import { buildPackageCountdownReminderSpeech } from '@/utils/packageCountdownReminder'
import { parsePropertyWithStatus } from '@/utils/parser'
// import { useRouter } from "vue-router"

interface MessageHandler {
  handle: (message: MessageRecordType) => Promise<void>
  getContent: (message: MessageRecordType) => string
}

function parseJsonObject(value: string): Record<string, unknown> | undefined {
  if (!value || !/^\s*\{/.test(value)) {
    return undefined
  }
  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed as Record<string, unknown>
      : undefined
  }
  catch {
    return undefined
  }
}

function pickString(record: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = record[key]
    if (typeof value === "string" && value.trim()) {
      return value
    }
  }
  return undefined
}

// const router = useRouter()
// 桌台预订处理器
const TableReservationHandler: MessageHandler = {
  async handle(message: MessageRecordType) {
    // 处理桌台预订消息的逻辑
    console.log(message)
    // handleOpenTable({
    //   reserveTableId: 2946,
    // } as any)
  },
  getContent(message: MessageRecordType) {
    return message.messageContent
  },
}

// 库存预警处理器
const StockWarningHandler: MessageHandler = {
  async handle(message: MessageRecordType) {
    // 处理库存预警消息的逻辑
    console.log(message)
    // router.push({
    //   name: 'StockWarning',
    // })
  },
  getContent(message: MessageRecordType) {
    return message.messageContent
  },
}

// 预订过期处理器
const ReservationExpiredHandler: MessageHandler = {
  async handle(message: MessageRecordType) {
    // 处理预订过期消息的逻辑
    console.log(message)
  },
  getContent(message: MessageRecordType) {
    // MerchantOmsTableReservation
    const { status, result } = parsePropertyWithStatus(message.messageContent, 'booker')
    if (status === 'success') {
      return `【${result}】预约已过期，请尽快处理`
    } else {
      return result
    }
  },
}

// 服务呼叫处理器
const CallServiceHandler: MessageHandler = {
  async handle(message: MessageRecordType) {
    // 处理服务呼叫消息的逻辑
    console.log(message)
  },
  getContent(message: MessageRecordType) {
    return message.messageContent
  },
}

// 用户点单处理器
const UserOrderHandler: MessageHandler = {
  async handle(message: MessageRecordType) {
    // 处理用户点单消息的逻辑
    console.log(message)
  },
  getContent(message: MessageRecordType) {
    // tableId + tableRecordId 的 map
    return message.messageContent
  },
}

// 已自动预结账处理器
const PreCheckCompleteHandler: MessageHandler = {
  async handle(message: MessageRecordType) {
    console.log(message)
  },
  getContent(message: MessageRecordType) {
    // MerchantTable
    const { status, result } = parsePropertyWithStatus(message.messageContent, 'tableName')
    if (status === 'success') {
      return `【${result}】已自动预结账，请尽快处理`
    } else {
      return result
    }
  },
}

// 预结账时间剩余15分钟处理器
const PreCheckoutReminderHandler: MessageHandler = {
  async handle(message: MessageRecordType) {
    console.log(message)
  },
  getContent(message: MessageRecordType) {
    const { status, result } = parsePropertyWithStatus(message.messageContent, 'tableName')
    if (status === 'success') {
      return `【${result}】剩余15分钟，请及时通知客户`
    } else {
      return result
    }
  },
}

// 包段倒计时提醒处理器
const PackageCountdownReminderHandler: MessageHandler = {
  async handle(message: MessageRecordType) {
    console.log(message)
  },
  getContent(message: MessageRecordType) {
    const payload = parseJsonObject(message.messageContent)
    const renderedContent = payload && pickString(payload, ["renderedContent"])
    if (renderedContent) {
      return renderedContent
    }
    const tableName = parsePropertyWithStatus(message.messageContent, 'tableName')
    const remainingMinutes = parsePropertyWithStatus(message.messageContent, 'remainingMinutes')
    if (tableName.status === 'success' && remainingMinutes.status === 'success' && tableName.result !== "--" && remainingMinutes.result !== "--") {
      return buildPackageCountdownReminderSpeech(tableName.result, Number(remainingMinutes.result))
    }
    return message.messageContent
  },
}

// 积分兑换提醒处理器
const CreditExchangeNotificationHandler: MessageHandler = {
  async handle(message: MessageRecordType) {
    console.log(message)
  },
  getContent(message: MessageRecordType) {
    return message.messageContent
  },
}

// 暂停完成处理器
const PauseCompleteHandler: MessageHandler = {
  async handle(message: MessageRecordType) {
    console.log(message)
  },
  getContent(message: MessageRecordType) {
    // MerchantTable
    const { status, result } = parsePropertyWithStatus(message.messageContent, 'tableName')
    if (status === 'success') {
      return `【${result}】暂停完成，请尽快处理`
    } else {
      return result
    }
  },
}

// 小茶助手主动消息处理器
const XiaochaAssistantHandler: MessageHandler = {
  async handle(message: MessageRecordType) {
    console.log(message)
  },
  getContent(message: MessageRecordType) {
    const payload = parseJsonObject(message.messageContent)
    if (!payload) {
      return message.messageContent
    }
    return pickString(payload, ["content", "messageContent", "message", "description", "summary"])
      || message.messageContent
  },
}

// 消息处理器映射
export const messageHandlers: Record<ValueOf<typeof MessageTypeConstants>, MessageHandler> = {
  [MessageTypeConstants.MESSAGE_TYPE_TABLE_RESERVATION]: TableReservationHandler,
  [MessageTypeConstants.MESSAGE_TYPE_STOCK_WARNING]: StockWarningHandler,
  [MessageTypeConstants.MESSAGE_TYPE_RESERVATION_EXPIRED]: ReservationExpiredHandler,
  [MessageTypeConstants.MESSAGE_TYPE_CALL_SERVICE]: CallServiceHandler,
  [MessageTypeConstants.MESSAGE_TYPE_USER_ORDER]: UserOrderHandler,
  [MessageTypeConstants.MESSAGE_TYPE_PRE_CHECK_COMPLETE]: PreCheckCompleteHandler,
  [MessageTypeConstants.MESSAGE_TYPE_PAUSE_COMPLETE]: PauseCompleteHandler,
  [MessageTypeConstants.MESSAGE_TYPE_PRE_CHECK_REMINDER]: PreCheckoutReminderHandler,
  [MessageTypeConstants.MESSAGE_TYPE_CREDIT_EXCHANGE_NOTIFICATION]: CreditExchangeNotificationHandler,
  [MessageTypeConstants.MESSAGE_TYPE_PACKAGE_COUNTDOWN_REMINDER]: PackageCountdownReminderHandler,
  [MessageTypeConstants.MESSAGE_TYPE_XIAOCHA_ASSISTANT]: XiaochaAssistantHandler,
}

// 统一的消息处理函数
export async function handleMessage(message: MessageRecordType): Promise<void> {
  const handler = messageHandlers[message.messageType]
  if (!handler) {
    throw new Error(`未找到消息类型 ${message.messageType} 的处理方法`)
  }
  await handler.handle(message)
}

export function getMessageContent(message: MessageRecordType): string {
  const handler = messageHandlers[message.messageType]
  if (!handler) {
    throw new Error(`未找到消息类型 ${message.messageType} 的处理方法`)
  }
  return handler.getContent(message)
}
